import React, { useState, useRef } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import LocationCarousel from '../components/userProfile/LocationCarousel';
import CategoryProgressCard from '../components/userProfile/CategoryProgressCard';
import SpeciesCard from '../components/shared/SpeciesCard';

const UserProfilePage = () => {
  const [selectedFilter, setSelectedFilter] = useState('Alla arter');
  const categoryScrollRef = useRef(null);

  // TODO: Replace with actual API calls to fetch user data
  const mockLocations = [];
  const mockCategories = [];
  const mockSpecies = [];

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 200;
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const filteredSpecies = selectedFilter === 'Alla arter' 
    ? mockSpecies 
    : mockSpecies.filter(s => s.category === selectedFilter);

  const filters = ['Alla arter', ...new Set(mockSpecies.map(s => s.category))];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      {/* Spacer for fixed Navbar */}
      <div className="h-20 w-full bg-white"></div>

      {/* Header Section */}
      <div className="w-full bg-white py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-forest mb-2">Min Profil</h1>
          <p className="text-base md:text-lg font-body text-gray-600 max-w-2xl mx-auto">
            Samla arter i olika naturområden och lås upp quiz
          </p>
        </div>
      </div>

      <div className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16 w-full">
        
        {/* 1. Header — Species Found per Nature Area */}
        <section className="relative">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 px-1">Dina Naturområden</h2>
          <LocationCarousel locations={mockLocations} />
        </section>

        {/* 2. Collections by category */}
        <section className="relative group">
          <div className="flex items-center justify-between mb-6 px-1">
            <h2 className="text-2xl font-bold text-gray-900">Samlingar</h2>
            <div className="hidden md:flex gap-2">
              <button 
                onClick={() => scrollCategories('left')}
                className="p-2 rounded-full bg-white shadow-sm hover:bg-gray-50 border border-gray-200 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button 
                onClick={() => scrollCategories('right')}
                className="p-2 rounded-full bg-white shadow-sm hover:bg-gray-50 border border-gray-200 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          
          <div 
            ref={categoryScrollRef}
            className="flex overflow-x-auto gap-4 pb-4 snap-x scrollbar-hide px-1"
          >
            {mockCategories.map(category => (
              <CategoryProgressCard key={category.id} category={category} />
            ))}
          </div>
        </section>

        {/* 3. Species Grid for a Selected Area */}
        <section>
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4 px-1">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Arter i Falsterbo Strandbad</h2>
              <p className="text-gray-500 text-sm mt-1">Visar {filteredSpecies.length} arter</p>
            </div>
            
            <div className="flex overflow-x-auto pb-2 gap-2 scrollbar-hide">
              {filters.map(filter => (
                <button
                  key={filter}
                  onClick={() => setSelectedFilter(filter)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedFilter === filter 
                      ? 'bg-green-600 text-white shadow-sm' 
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 mb-8">
            {filteredSpecies.map(species => (
              <SpeciesCard key={species.id} variant="grid" species={species} />
            ))}
          </div>

          {/* 4. “Keep Exploring” Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 p-3 rounded-full text-green-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Fortsätt samla!</h3>
                <p className="text-gray-600">
                  Du behöver hitta <span className="font-bold text-green-600">27 arter</span> till för att låsa upp quizet för <span className="font-bold">Falsterbo Strandbad</span>.
                </p>
              </div>
            </div>
            <button className="w-full md:w-auto bg-green-600 text-white px-6 py-3 rounded-full font-bold shadow-md hover:bg-green-700 transition-colors whitespace-nowrap">
              Gå till kartan
            </button>
          </div>
        </section>

      </div>
      <Footer />
    </div>
  );
};

export default UserProfilePage;
